import Optional from 'optional-js';

//@formatter:off
/**
 * @ngdoc service
 * @name uibDropdownToggleDecorator
 * @module portaro.decorators
 *
 * @description
 * Decorates [uib-dropdown](https://angular-ui.github.io/bootstrap/#dropdown) directive.
 * Add `:hover` behaviour.
 * If element contains `kp-dropdown-on-hover` attribute, hover behaviour is enabled
 *
 * @example
 * <example module="uibDropdownToggleDecoratorExample" name="uibDropdownToggleDecoratorExample" frame-no-resize="true">
    <file name="index.html">
      <span uib-dropdown kp-dropdown-on-hover>
          <a href uib-dropdown-toggle>Hover Me!</a>
          <ul uib-dropdown-menu>
               <li>
                   <a href="#">First</a>
               </li>
               <li>
                   <a href="#">Second</a>
               </li>
               <li>
                   <a href="#">Third</a>
               </li>
          </ul>
      </span>
    </file>
    <file name="script.js">
        angular.module('uibDropdownToggleDecoratorExample', ['ui.bootstrap', 'portaro.decorators']);
    </file>
 * </example>
 */
//@formatter:on

/*@ngInject*/
export default function uibDropdownToggleDecorator($provide) {
    $provide.decorator('uibDropdownDirective', decorator);

    /*@ngInject*/
    function decorator($delegate, $timeout) {

        let counter = 0;
        const directive = $delegate[0];
        const link = directive.link;
        let dontToggle = false;

        directive.compile = () =>
            ($scope, $elem, attrs, ctrl) => {
                // eslint-disable-next-line no-invalid-this
                link.apply(this, [$scope, $elem, attrs, ctrl]);

                // Hover behaviour
                if (Optional.ofNullable(attrs.kpDropdownOnHover).isPresent()) {

                    let timeoutPromise, touch = false;

                    const toggle = () => {
                        $scope.$apply(() => {
                            ctrl.toggle();
                        });
                    };

                    const hideHandler = () => {
                        $elem.off('mouseleave', hideHandler);

                        if (!dontToggle) {
                            timeoutPromise = $timeout(() => {
                                counter--;

                                if (counter === 0) {
                                    toggle();
                                }
                            }, 30);
                        } else {
                            dontToggle = false;
                        }
                    };

                    const showHandler = () => {
                        if (touch) {
                            touch = false;
                            return;
                        }

                        if (timeoutPromise && timeoutPromise.$$state.status === 0) {
                            $timeout.cancel(timeoutPromise);
                        } else {
                            counter++;
                            toggle();
                        }

                        $elem.on('mouseleave', hideHandler);

                        $elem.on('click', () => {
                            $elem.off('mouseleave', hideHandler);
                        });
                    };

                    const menuShowHandler = () => {
                        // $$state.status: 0 = active, 1 = success, 2 = cancelled
                        if (timeoutPromise && timeoutPromise.$$state.status === 0) {
                            $timeout.cancel(timeoutPromise);
                        }
                    };

                    const touchHandler = () => {
                        touch = true;
                    };

                    const menuClickHandler = () => {
                        dontToggle = true;
                        counter = 0;
                    };

                    ctrl.dropdownMenu
                        .on('mouseenter', menuShowHandler)
                        .on('mouseleave', hideHandler)
                        .on('click', menuClickHandler);

                    $elem
                        .on('mouseenter', showHandler)
                        .on('touchstart', touchHandler);

                    // cleanup
                    $scope.$on('$destroy', () => {
                        ctrl.dropdownMenu
                            .off('mouseenter', menuShowHandler)
                            .off('mouseleave', hideHandler)
                            .off('click', menuClickHandler);

                        $elem
                            .off('mouseenter', showHandler)
                            .off('touchstart', touchHandler);
                    });

                }

                // Fix add/remove class issue with append-to-body mode
                if (Optional.ofNullable(attrs.dropdownAppendToBody).isPresent()) {
                    const deregisterWatch = $scope.$watch(() => ctrl.isOpen(), (isOpen) => {
                        if (isOpen) {
                            $elem.addClass('open onhover');
                        } else {
                            $elem.removeClass('open onhover');
                        }
                    });

                    // Cleanup
                    $scope.$on('$destroy', () => {
                        deregisterWatch();
                    });
                }
            };
        return $delegate;
    }
}